import {SHIFT_DISTANCE} from './RewardsAnimationsService.constants';
import {DI_TOKENS} from '@/di/di.tokens';
import {Point} from 'pixi.js';

import anime from 'animejs';
import {injected} from 'brandi';

import {GCSEngine} from '@specials/gcs-engine';

import {HTMLElementId} from '@/constants';
import {RealRandom} from '@/utils/random/RealRandom';

interface CrystalListPositionList {
  phase1TargetPosition: Point;
  phase2TargetPosition: Point;
}

export class RewardsAnimationsService {
  constructor(private readonly random: RealRandom) {}

  public async animateCrystalRewards(startTargetId: string, onAnimationEnd?: VoidFunction): Promise<void> {
    // собираем скрытые ноды кристалов
    const crystalList = Array.from(document.getElementsByClassName(HTMLElementId.AnimationCrystal)) as HTMLElement[];
    // кристал в счетчик, в который должен лететь
    const targetCrystalNode = document.getElementById(HTMLElementId.ClickerGamePointsCrystal);

    const startTargetNode = document.getElementById(startTargetId);

    if (!targetCrystalNode || !startTargetNode) return;

    const {
      x: targetX,
      y: targetY,
      width: targetWidth,
      height: targetHeight,
    } = targetCrystalNode.getBoundingClientRect();
    // центр кристала, в который должен прилететь кристал из модалки
    const targetCenter = new Point(targetX + targetWidth / 2, targetY + targetHeight / 2);

    // ставим позиции кристаллов на позицию элемента, из которого они будут вылетать
    const {x, y} = startTargetNode.getBoundingClientRect();
    crystalList.forEach((node) => {
      node.style.top = y + 'px';
      node.style.left = x + 'px';
      node.style.transform = 'unset';
    });

    const positionList = this.getCrystalPositionList(crystalList, targetCenter);

    await anime({
      targets: crystalList,
      translateX: (_: unknown, i: number) => positionList[i].phase1TargetPosition.x,
      translateY: (_: unknown, i: number) => positionList[i].phase1TargetPosition.y,
      opacity: 1,
      duration: 500,
      delay: anime.stagger(50),
      easing: 'easeOutQuad',
    }).finished;

    await Promise.all([
      anime({
        targets: crystalList,
        translateX: (_: unknown, i: number) => positionList[i].phase2TargetPosition.x,
        translateY: (_: unknown, i: number) => positionList[i].phase2TargetPosition.y,
        duration: 750,
        delay: anime.stagger(100),
        easing: 'easeInQuad',
      }).finished,
      anime({
        targets: crystalList,
        opacity: 0,
        duration: 50,
        delay: 700,
        easing: 'easeInCubic',
      }).finished,
    ]);

    onAnimationEnd?.();
  }

  public async animateRechargeRewards(): Promise<void> {
    const animatedRecharge = document.getElementById(HTMLElementId.RewardsAnimationRecharge);
    const targetRecharge = document.getElementById(HTMLElementId.ClickerGameRecharge);
    const modal = document.getElementById(HTMLElementId.ModalDetailContent);

    if (!animatedRecharge || !targetRecharge || !modal) return;

    await anime({
      targets: modal,
      translateY: -100,
      duration: 250,
      easing: 'easeInQuad',
    }).finished;

    const {x, y} = animatedRecharge.getBoundingClientRect();
    const {x: targetX, y: targetY} = targetRecharge.getBoundingClientRect();

    animatedRecharge.style.opacity = '1';

    anime({
      targets: animatedRecharge,
      opacity: [1, 0],
      delay: 400,
      duration: 100,
      easing: 'easeInQuad',
    });
    await anime({
      targets: animatedRecharge,
      translateX: targetX - x,
      translateY: targetY - y,
      width: 41,
      height: 56,
      duration: 500,
      easing: 'easeInQuad',
    }).finished;
  }

  public async animateGiftReward(onAnimationEnd: VoidFunction): Promise<void> {
    const giftsIcon = document.getElementById(HTMLElementId.HeaderGiftsIcon);
    const animatedNoticeBadge = document.getElementById(HTMLElementId.RewardsBadge);
    const targetNoticeBadge = document.getElementById(HTMLElementId.HeaderNoticeBadge);
    const startTarget = document.getElementById(HTMLElementId.RewardsGift);

    if (!giftsIcon || !animatedNoticeBadge || !targetNoticeBadge || !startTarget) return;

    const {
      x: startTargetX,
      y: startTargetY,
      width: startTargetWidth,
      height: startTargetHeight,
    } = startTarget.getBoundingClientRect();

    animatedNoticeBadge.style.left = startTargetX + startTargetWidth / 2 + 'px';
    animatedNoticeBadge.style.top = startTargetY + startTargetHeight + 'px';

    const {x, y} = animatedNoticeBadge.getBoundingClientRect();
    const {x: targetX, y: targetY} = targetNoticeBadge.getBoundingClientRect();

    anime({
      targets: animatedNoticeBadge,
      opacity: 1,
      duration: 400,
      easing: 'easeInCubic',
    });

    await Promise.all([
      anime({
        targets: animatedNoticeBadge,
        translateX: targetX - x,
        translateY: targetY - y,
        duration: 1000,
        easing: 'easeInQuad',
        complete: () => {
          animatedNoticeBadge.style.opacity = '0';
        },
      }).finished,
      anime({
        targets: giftsIcon,
        scale: 1.4,
        duration: 1000,
        easing: 'easeInQuad',
        direction: 'alternate',
      }).finished,
    ]);

    onAnimationEnd();
  }

  private getCrystalPositionList(crystalList: HTMLElement[], target: Point): CrystalListPositionList[] {
    return crystalList.map((node) => {
      const nodePhase1Position = new Point(
        this.random.generateIntegerNumber(-SHIFT_DISTANCE, SHIFT_DISTANCE),
        this.random.generateIntegerNumber(-SHIFT_DISTANCE, SHIFT_DISTANCE),
      );

      const {x, y, width, height} = node.getBoundingClientRect();

      // расчет для второй фазы, учитывая смещение после первой
      const nodeCenter = new Point(nodePhase1Position.x + x + width / 2, nodePhase1Position.y + y + height / 2);

      const angleInRad = GCSEngine.Vector2Utils.getAngleBetweenVectors(nodeCenter, target);
      const length = GCSEngine.Vector2Utils.distance(nodeCenter, target);

      const nodePhase2Position = new Point(
        nodePhase1Position.x + Math.cos(angleInRad) * length,
        nodePhase1Position.y + Math.sin(angleInRad) * length,
      );

      return {
        phase1TargetPosition: nodePhase1Position,
        phase2TargetPosition: nodePhase2Position,
      };
    });
  }
}

injected(RewardsAnimationsService, DI_TOKENS.realRandom);
